import { useState, useCallback } from 'react';

// Google Maps API Pricing - 2025
// https://developers.google.com/maps/billing-and-pricing/pricing
// Per-request costs (calculated from per-10k or per-5k pricing)
const COST_PER_REQUEST = {
  geocoding: 0.0005,          // $5 per 10,000 = $0.0005/request
  streetViewStatic: 0.0007,   // $7 per 10,000 = $0.0007/image
  streetViewDynamic: 0.0028,  // $14 per 5,000 = $0.0028/panorama
  mapsJavaScript: 0.0007,     // $7 per 10,000 = $0.0007/load (Dynamic Maps)
  placesAutocomplete: 0.000283, // $2.83 per 10,000 = $0.000283/session
  mapsEmbed: 0,               // Maps Embed API is free (unlimited)
};

export function useApiCostTracker() {
  const [apiCalls, setApiCalls] = useState({
    geocoding: 0,
    streetViewStatic: 0,
    streetViewDynamic: 0,
    mapsJavaScript: 1, // Initial map load
    placesAutocomplete: 0,
  });

  const trackApiCall = useCallback((apiType, count = 1) => {
    setApiCalls(prev => ({
      ...prev,
      [apiType]: (prev[apiType] || 0) + count
    }));
  }, []);

  const resetTracking = useCallback(() => {
    setApiCalls({
      geocoding: 0,
      streetViewStatic: 0,
      streetViewDynamic: 0,
      mapsJavaScript: 1,
      placesAutocomplete: 0,
    });
  }, []);

  // Calculate costs using per-request pricing
  const costs = {
    geocoding: apiCalls.geocoding * COST_PER_REQUEST.geocoding,
    streetViewStatic: apiCalls.streetViewStatic * COST_PER_REQUEST.streetViewStatic,
    streetViewDynamic: apiCalls.streetViewDynamic * COST_PER_REQUEST.streetViewDynamic,
    mapsJavaScript: apiCalls.mapsJavaScript * COST_PER_REQUEST.mapsJavaScript,
    placesAutocomplete: apiCalls.placesAutocomplete * COST_PER_REQUEST.placesAutocomplete,
  };

  const totalCost = Object.values(costs).reduce((sum, cost) => sum + cost, 0);
  const totalCalls = Object.values(apiCalls).reduce((sum, calls) => sum + calls, 0);

  return {
    apiCalls,
    costs,
    totalCost,
    totalCalls,
    trackApiCall,
    resetTracking,
    COST_PER_REQUEST
  };
}

export default useApiCostTracker;
